import { browserLocalPersistence, onAuthStateChanged, sendPasswordResetEmail, setPersistence, signInWithEmailAndPassword, signOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { getFirebaseAuth, getFirebaseFunctions } from './firebase';

export async function login(username, password) {
  await setPersistence(getFirebaseAuth(), browserLocalPersistence);
  const credential = await signInWithEmailAndPassword(getFirebaseAuth(), username, password);
  return getUserFromFirebaseUser(credential.user, username);
}

export async function logout() {
  await signOut(getFirebaseAuth());
}

export function subscribeToCurrentUser(onChange) {
  return onAuthStateChanged(getFirebaseAuth(), async (firebaseUser) => {
    if (!firebaseUser) {
      onChange(null);
      return;
    }

    onChange(await getUserFromFirebaseUser(firebaseUser));
  });
}

export async function resetPassword(username) {
  try {
    await sendPasswordResetEmail(getFirebaseAuth(), username);
  } catch (error) {
    if (!isPasswordResetPrivacySafeError(error)) {
      throw error;
    }
  }
}

export async function searchUploads(searchQuery = '') {
  const search = httpsCallable(getFirebaseFunctions(), 'searchUploads');
  const result = await search({ query: searchQuery });
  return Array.isArray(result.data?.uploads) ? result.data.uploads.map(normalizeUpload) : [];
}

export async function getUpload(code) {
  const getReview = httpsCallable(getFirebaseFunctions(), 'getUploadReview');
  const result = await getReview({ code });
  return result.data ? normalizeUpload(result.data) : null;
}

export async function updateImagePrintVersion(code, imageId, printVersion) {
  const setVersion = httpsCallable(getFirebaseFunctions(), 'setImagePrintVersion');
  await setVersion({ code, imageId, printVersion });
  return getUpload(code);
}

export async function createDownloadRequest(code) {
  const createZip = httpsCallable(getFirebaseFunctions(), 'createDownloadZip');
  const result = await createZip({ code });

  return {
    code,
    status: 'ready',
    downloadUrl: result.data?.downloadUrl,
    message: result.data?.downloadUrl
      ? `ZIP ready for ${code}. The signed link expires in ${result.data.expiresInMinutes || 15} minutes.`
      : `ZIP request prepared for ${code}.`,
  };
}

export async function listUsers() {
  const list = httpsCallable(getFirebaseFunctions(), 'listAdminUsers');
  const result = await list();
  return Array.isArray(result.data?.users) ? result.data.users : [];
}

export async function addUser(user) {
  const createUser = httpsCallable(getFirebaseFunctions(), 'createAdminUser');
  const result = await createUser({
    email: user.username,
    role: user.role,
  });

  return {
    passwordResetEmailSent: Boolean(result.data?.passwordResetEmailSent),
    users: Array.isArray(result.data?.users) ? result.data.users : [],
  };
}

export async function removeUser(id) {
  const deleteUser = httpsCallable(getFirebaseFunctions(), 'deleteAdminUser');
  const result = await deleteUser({ uid: id });
  return Array.isArray(result.data?.users) ? result.data.users : [];
}

export async function updateUserRole(id, role) {
  const setRole = httpsCallable(getFirebaseFunctions(), 'setUserRole');
  await setRole({ uid: id, role });
  return listUsers();
}

export async function listCleanupReports() {
  const list = httpsCallable(getFirebaseFunctions(), 'listCleanupReports');
  const result = await list();
  return Array.isArray(result.data?.reports) ? result.data.reports.map(normalizeCleanupReport) : [];
}

function normalizeUpload(upload) {
  return {
    ...upload,
    code: upload.code || upload.id,
    createdAt: toDateString(upload.createdAt),
    expiresAt: toDateString(upload.expiresAt),
    imageCount: upload.imageCount || upload.images?.length || 0,
    images: Array.isArray(upload.images) ? upload.images.map(normalizeImage) : [],
  };
}

function normalizeImage(image) {
  return {
    ...image,
    createdAt: toDateString(image.createdAt),
  };
}

function normalizeCleanupReport(report) {
  return {
    ...report,
    startedAt: toDateString(report.startedAt),
    finishedAt: toDateString(report.finishedAt),
    metrics: report.metrics || {},
  };
}

async function getUserFromFirebaseUser(firebaseUser, fallbackUsername = '') {
  const token = await firebaseUser.getIdTokenResult(true);

  return {
    id: firebaseUser.uid,
    username: firebaseUser.email || fallbackUsername,
    role: token.claims.role || 'staff',
  };
}

function isPasswordResetPrivacySafeError(error) {
  return [
    'auth/invalid-email',
    'auth/missing-email',
    'auth/user-not-found',
  ].includes(error?.code);
}

function toDateString(value) {
  if (!value) {
    return new Date().toISOString();
  }

  if (typeof value === 'string') {
    return isValidDate(value) ? new Date(value).toISOString() : new Date().toISOString();
  }

  if (typeof value.toDate === 'function') {
    const date = value.toDate();
    return isValidDate(date) ? date.toISOString() : new Date().toISOString();
  }

  if (typeof value === 'number') {
    const date = new Date(value);
    return isValidDate(date) ? date.toISOString() : new Date().toISOString();
  }

  if (typeof value === 'object') {
    const seconds = Number(value.seconds ?? value._seconds);

    if (Number.isFinite(seconds)) {
      const date = new Date(seconds * 1000);
      return isValidDate(date) ? date.toISOString() : new Date().toISOString();
    }
  }

  const date = new Date(value);
  return isValidDate(date) ? date.toISOString() : new Date().toISOString();
}

function isValidDate(value) {
  const date = value instanceof Date ? value : new Date(value);
  return !Number.isNaN(date.getTime());
}
